import React, { useEffect, useRef, useState } from 'react'
import { useRetorikStore } from '../../Contexts/RetorikContext'
import { useViewStore } from '../../Contexts/ViewContext'
import type {
  HistoryActivity,
  RetorikActivity
} from '../../../models/activityTypes'
import { DeviceType } from '../../../models/enums'
import ConversationDisplay from './ConversationDisplay'

interface ConversationPanelLargeProps {
  activities: Array<HistoryActivity>
  itemsInConversation: number
}

const ConversationPanelLarge = ({
  activities,
  itemsInConversation
}: ConversationPanelLargeProps): JSX.Element => {
  const configuration = useRetorikStore((state) => state.configuration)
  const currentDeviceType = useViewStore((state) => state.currentDeviceType)
  const mediumLandscape = useViewStore((state) => state.mediumLandscape)
  const isLandscape = useViewStore((state) => state.isLandscape)

  const [userQuestion, setUserQuestion] = useState<RetorikActivity | undefined>(
    undefined
  )
  const [botResponses, setBotResponses] = useState<Array<RetorikActivity>>([])

  const retorikConversationRef = useRef<HTMLDivElement>(null)
  const botEndRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const isLandscapeRef = useRef<boolean>(true)

  const [gridRowStart, setGridRowStart] = useState<number>()
  const [gridRowSpan, setGridRowSpan] = useState<number>()
  const [opacityStyle, setOpacityStyle] = useState<string | undefined>('')

  useEffect(() => {
    setOpacityStyle(undefined)
  }, [activities?.length])

  useEffect(() => {
    switch (currentDeviceType) {
      case DeviceType.landscape:
      case DeviceType.widgetLandscape: {
        isLandscapeRef.current = true
        if (
          typeof configuration?.answerpanel === 'object' &&
          configuration.answerpanel.landscape
        ) {
          const start =
            configuration.answerpanel.landscape.containerRowStartText
          const end = configuration.answerpanel.landscape.containerRowEndText

          if (start && start > 0 && start < 11) {
            if (end && end < 11) {
              if (start < end) {
                setGridRowStart(start)
                setGridRowSpan(end - start)
              }
            } else {
              setGridRowStart(start)
              setGridRowSpan(11 - start)
            }
          } else if (end && end > 0 && end < 11) {
            setGridRowStart(1)
            setGridRowSpan(end - 1)
          } else {
            setGridRowStart(1)
            setGridRowSpan(configuration.useOldRemote ? 10 : 9)
          }
        } else {
          setGridRowStart(1)
          setGridRowSpan(configuration.useOldRemote ? 10 : 9)
        }
        break
      }
      case DeviceType.borne:
      case DeviceType.widgetBorne: {
        isLandscapeRef.current = false
        if (
          typeof configuration.answerpanel === 'object' &&
          configuration.answerpanel.borne
        ) {
          const start = configuration.answerpanel.borne.containerRowStart
          const end = configuration.answerpanel.borne.containerRowEnd

          if (start && start > 0 && start < 13) {
            if (end && end < 13) {
              if (start < end) {
                setGridRowStart(start)
                setGridRowSpan(end - start)
              }
            } else {
              setGridRowStart(start)
              setGridRowSpan(13 - start)
            }
          } else if (end && end > 0 && end < 13) {
            setGridRowStart(1)
            setGridRowSpan(end - 1)
          } else {
            setGridRowStart(6)
            setGridRowSpan(7)
          }
        } else {
          setGridRowStart(6)
          setGridRowSpan(7)
        }
        break
      }
    }
  }, [currentDeviceType, configuration.answerpanel, configuration.useOldRemote])

  useEffect(() => {
    if (activities.length) {
      const lastActivity = activities[activities.length - 1]

      setUserQuestion(lastActivity.question)
      setBotResponses([...lastActivity.answers])
    } else {
      setUserQuestion(undefined)
      setBotResponses([])
    }
  }, [activities, itemsInConversation])

  const handleScroll = (): void => {
    botEndRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    })

    handleScrollOpacity(false)
  }

  const handleScrollOpacity = (triggeredByUser: boolean): void => {
    if (retorikConversationRef.current) {
      const current = retorikConversationRef.current as HTMLDivElement

      if (current.scrollHeight > current.clientHeight + 5) {
        let percentage = 0
        if (current.scrollTop || !triggeredByUser) {
          percentage = Math.ceil(
            ((current.scrollHeight -
              current.clientHeight -
              Math.abs(current.scrollTop)) *
              100) /
              current.scrollHeight
          )

          !isLandscapeRef.current && (percentage = Math.max(25 - percentage, 0))

          setOpacityStyle(
            `linear-gradient(to top, black ${
              100 - Math.min(percentage, 15)
            }%, transparent 100%)`
          )
        }
      } else {
        setOpacityStyle('linear-gradient(to top, black 100%, transparent 100%)')
      }
    }
  }

  useEffect(() => {
    timerRef.current && clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      handleScroll()
    }, 100)

    return (): void => {
      timerRef.current && clearTimeout(timerRef.current)
    }
  }, [itemsInConversation])

  return userQuestion || botResponses ? (
    <div
      id='retorik-conversation'
      ref={retorikConversationRef}
      className={`rf-relative rf-w-full rf-max-h-full rf-z-util landscape-retorik:rf-pb-2 ${
        mediumLandscape
          ? 'landscape-retorik:rf-col-start-3'
          : 'landscape-retorik:rf-col-start-4'
      } vertical:rf-col-start-1 rf-col-end-13 rf-self-start rf-overflow-y-scroll rf-overflow-x-visible rf-scrollbar-hidden vertical:rf-overflow-x-auto rf-justify-self-center rf-flex rf-flex-col landscape-retorik:rf-flex-col-reverse landscape-retorik:rf-self-end rf-gap-4 rf-text-size-auto rf-text-trueblack`}
      style={{
        gridRow: `${gridRowStart} / span ${gridRowSpan}`,
        WebkitMaskImage: opacityStyle
      }}
      onScroll={() => handleScrollOpacity(true)}
    >
      {isLandscape && <div ref={botEndRef} />}

      <ConversationDisplay
        userQuestion={userQuestion}
        botResponses={botResponses}
        triggerScroll={handleScroll}
        canBeStreaming={true}
      />

      {!isLandscape && <div ref={botEndRef} />}
    </div>
  ) : (
    <React.Fragment />
  )
}

export default ConversationPanelLarge
